import type {
  CTAGroupAction,
  CTAGroupAlignment,
  CTAGroupButtonWidth,
  CTAGroupWidthConfig,
} from "@/components/CTAGroup/types";
import type { LazyIconName } from "@/components/icons/LazyIcon";
import type { TAGULAR_ELEMENT } from "@/types/tagular";
import { ButtonTone } from "../Button/types";

export type ValuePropImageType =
  | "white-xfinity-router"
  | "two-people-excited-for-risk-free"
  | "woman-smiling-on-phone-call"
  | "late-night-phone-call-laptop"
  | "person-laptop-orange-flowers"
  | "two-people-working-laptop-smartphone-outdoors";

export type ValuePropIconImageType = "peacock-premium-icon";

export type ValuePropMediaImageType =
  | ValuePropImageType
  | ValuePropIconImageType;

export type ValuePropsTitlePosition = "left" | "center";
export type ValuePropsTitleColor = "neutral-base" | "theme1-base";
export type ValuePropsBackgroundColor =
  | "white"
  | "theme-base"
  | "gradient-lavender";
export type ValuePropsCardStyle =
  | "transparent"
  | "white"
  | "theme-base"
  | "theme-bordered"
  | "translucent-light";
export type ValuePropsCardLayout = "default" | "horizontal";
export type ValuePropsMobileListIconPlacement = "top" | "left";

/**
 * Per-breakpoint text size override for card title, subtitle, and badge.
 * Each value maps to a semantic typography token from `globals.css`:
 *  - `xs`  -> tiny/xs scale (smallest)
 *  - `sm`  -> small scale
 *  - `base`-> base scale (component default)
 *  - `lg`  -> large scale
 *
 * Sizes are resolved per-breakpoint so editors can promote/demote text
 * independently at mobile, tablet, and desktop.
 */
export type ValuePropTextSize = "xs" | "sm" | "base" | "lg";

export interface ValuePropTextSizeBreakpoint {
  size?: ValuePropTextSize;
}

export interface ValuePropCopy {
  copy: string;
  mobile?: ValuePropTextSizeBreakpoint;
  tablet?: ValuePropTextSizeBreakpoint;
  desktop?: ValuePropTextSizeBreakpoint;
}

export interface ValuePropBadge {
  copy: string;
  mobile?: ValuePropTextSizeBreakpoint;
  tablet?: ValuePropTextSizeBreakpoint;
  desktop?: ValuePropTextSizeBreakpoint;
}

export type ValuePropMedia =
  | {
      type: "image";
      image: ValuePropImageType;
    }
  | {
      type: "icon";
      icon: LazyIconName;
    }
  | {
      type: "iconImage";
      image: ValuePropIconImageType;
    };

export interface ValuePropCard {
  title: ValuePropCopy;
  subtitle?: ValuePropCopy;
  badge?: ValuePropBadge;
  media: ValuePropMedia;
  elementName?: string;
}

export type ValuePropsMobileLayout = "auto" | "grid" | "list";

export interface ValuePropsCtaGroupItem {
  copy: string;
  mobileCopy?: string;
  action: CTAGroupAction;
  url?: string;
  modalKey?: string;
  ariaLabel?: string;
  tone?: ButtonTone;
  buttonWidth?: {
    mobile?: CTAGroupButtonWidth;
    tablet?: CTAGroupButtonWidth;
    desktop?: CTAGroupButtonWidth;
  };
}

export interface ValuePropsCtaGroup {
  alignment?: CTAGroupAlignment;
  ctas: ValuePropsCtaGroupItem[];
}

export const mapValuePropsCtaButtonWidth = (
  buttonWidth?: ValuePropsCtaGroupItem["buttonWidth"]
): CTAGroupWidthConfig | undefined => {
  if (!buttonWidth) {
    return undefined;
  }

  return {
    base: buttonWidth.mobile,
    md: buttonWidth.tablet,
    xl: buttonWidth.desktop,
  };
};

export interface ValuePropsLegal {
  copy: string;
}

/**
 * Approved keys for the optional `md+` featured image. Each key maps
 * to an entry in `VALUE_PROP_FEATURED_IMAGES` (alt text + intrinsic
 * dimensions live in the asset map, not in Monarch).
 */
export type ValuePropsFeaturedImageKey =
  | "mobile-rays-lifestyle"
  | "stream-lifestyle";

export interface ValuePropsFeaturedImage {
  /** Approved asset key from `VALUE_PROP_FEATURED_IMAGES`. */
  key: ValuePropsFeaturedImageKey;
  /**
   * Optional horizontal position relative to the cards on tablet/desktop (md+).
   * Defaults to `"right"` to match the Figma reference.
   */
  position?: "left" | "right";
}

export interface ValuePropsSectionProps {
  elementName: string;
  title: ValuePropCopy & {
    position?: ValuePropsTitlePosition;
    color?: ValuePropsTitleColor;
  };
  /**
   * Optional supporting copy displayed directly below the section title.
   * Shares the section title's alignment and color, and accepts the same
   * optional per-breakpoint `mobile`/`tablet`/`desktop` size overrides
   * available on every `ValuePropCopy` field.
   */
  subtitle?: ValuePropCopy;
  mobileLayout?: ValuePropsMobileLayout;
  mobileListIconPlacement?: ValuePropsMobileListIconPlacement;
  backgroundColor?: ValuePropsBackgroundColor;
  cardStyle?: ValuePropsCardStyle;
  cardLayout?: ValuePropsCardLayout;
  ctaGroup?: ValuePropsCtaGroup;
  legal?: ValuePropsLegal;
  cards: ValuePropCard[];
  /**
   * Optional featured image rendered alongside the cards from `md:` (>=768px).
   * Hidden below `md` so the existing responsive card layout is preserved on
   * mobile.
   */
  featuredImage?: ValuePropsFeaturedImage;
  tagularProps?: TAGULAR_ELEMENT;
}

export interface BaseValuePropsSectionData
  extends ValuePropsSectionProps {
  [key: string]: unknown;
}
